import React from 'react';
import { fetchWrapper } from '../../APIManager';
import { TYPE_IDS_TO_EXCLUDE, FILTERS_LIST } from '../../components/Constants';
const OverviewStateContext = /*#__PURE__*/React.createContext();
const OverviewDispatchContext = /*#__PURE__*/React.createContext();
const initialState = {
  notifications: [],
  unreadActivityCount: 0,
  unreadMessagesCount: 0,
  unreadMessagesSentCount: 0,
  messages: [],
  messagesSent: [],
  users: [],
  usersSent: []
};

/** REDUCER **/
function OverviewReducer(state, action) {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload
      };
    case 'SET_UNREAD_ACTIVITY_COUNT':
      return {
        ...state,
        unreadActivityCount: action.payload
      };
    case 'SET_USERS':
      return {
        ...state,
        users: action.payload
      };
    case 'SET_USERS_SENT':
      return {
        ...state,
        usersSent: action.payload
      };
    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload
      };
    case 'SET_MESSAGES_SENT':
      return {
        ...state,
        messagesSent: action.payload
      };
    case 'SET_UNREAD_MESSAGES_COUNT':
      return {
        ...state,
        unreadMessagesCount: action.payload
      };
    case 'SET_UNREAD_MESSAGES_SENT_COUNT':
      return {
        ...state,
        unreadMessagesSentCount: action.payload
      };
    case 'GET_INITIAL':
      return initialState;
    default:
      {
        throw new Error('Unhandled action type caught');
      }
  }
}

/** PROVIDER **/
function OverviewProvider(_ref) {
  let {
    value,
    children
  } = _ref;
  const [state, dispatch] = React.useReducer(OverviewReducer, initialState);
  return /*#__PURE__*/React.createElement(OverviewStateContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(OverviewDispatchContext.Provider, {
    value: dispatch
  }, children));
}

/** STATE/DISPATCH HOOKS **/
function useOverviewState() {
  const context = React.useContext(OverviewStateContext);
  if (context === undefined) {
    throw new Error('useOverviewState must be used within a OverviewProvider');
  }
  return context;
}
function useOverviewDispatch() {
  const context = React.useContext(OverviewDispatchContext);
  if (context === undefined) {
    throw new Error('useOverviewDispatch must be used within a OverviewProvider');
  }
  return context;
}

/** API **/
const overviewAPI = {
  /*
    also possible to load more notifications
    by using response.load_more_notifications
    no need for now
  */
  getNotifications: async function (url, authUserName, authUserAPIKey, moderator, product, allowedCategories, filter) {
    try {
      if (!FILTERS_LIST.includes(filter)) {
        throw new Error('Invalid filter provided');
      }
      const slug = `/notifications?username=${authUserName}&filter=${filter}`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response || !response.notifications || !response.notifications.length) {
        throw new Error('Could not get notifications data.');
      }

      // massage this
      const {
        notifications: data
      } = response;
      const notifications = data.filter(item => {
        return !TYPE_IDS_TO_EXCLUDE.includes(item.notification_type) && moderator ? true : allowedCategories.includes(item.category_id); // TODO: Note this will NOT work with DMs
      });
      /* I think the above might screw up DMs when we add them, so we might want something more like the below
       * but I am not sure if the below screws up notifications for tracking topics
       * so let's keep the above line for now */
      //const sortedNotifications = notifications.filter((item) => !SYSTEM_USER_NAMES.includes(item.data.notifying_user_name));

      return notifications;
    } catch (err) {
      return [];
    }
  },
  getPrivateMessages: async function (url, authUserName, authUserAPIKey) {
    try {
      const slug = `/topics/private-messages/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response) {
        throw new Error('Could not get private messages.');
      }
      return response;
    } catch (err) {
      return [];
    }
  },
  getPrivateMessagesSent: async function (url, authUserName, authUserAPIKey) {
    try {
      const slug = `/topics/private-messages-sent/${authUserName}.json`;
      const response = await fetchWrapper(url, slug, authUserAPIKey);
      if (!response) {
        throw new Error('Could not get private messages sent.');
      }
      return response;
    } catch (err) {
      return [];
    }
  },
  getBadge: async function (url, apiKey, badgeId) {
    try {
      const slug = `/badges/${badgeId}`;
      const response = await fetchWrapper(url, slug, apiKey);
      if (!response) {
        throw new Error('Could not get badge.');
      }
      return response;
    } catch (err) {
      return false;
    }
  },
  setActivityToRead: async function (url, apiKey, activityId) {
    try {
      const slug = '/notifications/mark-read.json';
      const data = {
        id: activityId
      };
      const res = await fetchWrapper(url, slug, apiKey, 'PUT', data);
      if (!res || res.success !== 'OK') {
        throw new Error('Failed to mark notification as read.');
      }
      return true;
    } catch (err) {
      return false;
    }
  }
};
export { OverviewProvider, useOverviewState, useOverviewDispatch, overviewAPI };
//# sourceMappingURL=index.js.map